import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface CartLineItem {
  productId: string;
  variantId: string;
  productName: string;
  variantLabel: string;
  slug: string;
  unitPrice: number; // cents (the effective price the customer pays per unit)
  quantity: number;
  isSubscription: boolean;
  frequencyDays?: number; // 30/60/90 when isSubscription
  imageUrl: string;
}

interface CartState {
  items: CartLineItem[];
  add: (item: CartLineItem) => void;
  remove: (productId: string, variantId: string) => void;
  setQuantity: (productId: string, variantId: string, qty: number) => void;
  toggleSubscription: (productId: string, variantId: string) => void;
  clear: () => void;
}

const sameLine = (a: CartLineItem, productId: string, variantId: string) =>
  a.productId === productId && a.variantId === variantId;

export const useCartStore = create<CartState>()(
  persist(
    set => ({
      items: [],
      add: item =>
        set(s => {
          const existing = s.items.find(i => sameLine(i, item.productId, item.variantId));
          if (existing) {
            return {
              items: s.items.map(i =>
                i === existing ? { ...i, quantity: i.quantity + item.quantity } : i
              ),
            };
          }
          return { items: [...s.items, item] };
        }),
      remove: (productId, variantId) =>
        set(s => ({ items: s.items.filter(i => !sameLine(i, productId, variantId)) })),
      setQuantity: (productId, variantId, qty) =>
        set(s => ({
          items:
            qty > 0
              ? s.items.map(i => (sameLine(i, productId, variantId) ? { ...i, quantity: qty } : i))
              : s.items.filter(i => !sameLine(i, productId, variantId)),
        })),
      toggleSubscription: (productId, variantId) =>
        set(s => ({
          items: s.items.map(i =>
            sameLine(i, productId, variantId) ? { ...i, isSubscription: !i.isSubscription } : i
          ),
        })),
      clear: () => set({ items: [] }),
    }),
    { name: 'drburns-cart-v1' }
  )
);
